import axios, { AxiosResponse } from "axios";
import { LOG_LEVELS, SendLog } from "./log";
import { RestClient } from "./rest-client";
import { ErrorResponse } from "./types/common.entity";

type ServiceErrorHandler = (params: {
  status: number;
  url?: string;
  eventName?: string;
}) => Promise<void>;

type ErrorEventName = string | ((status?: number) => string | undefined);

type RequestTemplateParams<TSuccess> = {
  url: string;
  errorUrl: string;
  onHttpErrorMessage: string;
  onUnknownErrorLogMessage: string;
  emptyData: TSuccess;
  handleError: ServiceErrorHandler;
  errorEventName?: ErrorEventName;
  successStatuses?: number[];
  additionalHeaders?: Record<string, string>;
};

export const requestGetWithTemplate = async <TSuccess>({
  url,
  errorUrl,
  onHttpErrorMessage,
  onUnknownErrorLogMessage,
  emptyData,
  handleError,
  errorEventName,
  successStatuses = [200, 202],
  additionalHeaders,
}: RequestTemplateParams<TSuccess>) => {
  const getErrorEventName = (status?: number) =>
    typeof errorEventName === "function"
      ? errorEventName(status)
      : errorEventName;

  try {
    const response = await RestClient.request<TSuccess | ErrorResponse>({
      method: "get",
      url,
      ignoreErrors: true,
      additionalHeaders,
    });

    if (successStatuses.includes(response.status)) {
      return {
        isSuccess: true,
        data: response.data as TSuccess,
        status: response.status,
      };
    }

    await handleError({
      status: response.status,
      url: errorUrl,
      eventName: getErrorEventName(response.status),
    });

    return {
      isSuccess: false,
      data: emptyData,
      status: response.status,
    };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const statusCode = error.response?.status;
      const response = error.response as AxiosResponse<ErrorResponse>;
      const errorMessage = error.message;

      console.error(onHttpErrorMessage, errorMessage);

      await handleError({
        status: response.status,
        url: errorUrl,
        eventName: getErrorEventName(response.status),
      });

      return {
        isSuccess: false,
        data: response.data as ErrorResponse,
        status: statusCode || 500,
      };
    }

    console.error("Unknown error occurred", error);
    const eventName = getErrorEventName();
    await SendLog(
      LOG_LEVELS.ERROR,
      ...(eventName ? [eventName] : []),
      onUnknownErrorLogMessage,
    );
    return {
      isSuccess: false,
      data: {} as ErrorResponse,
      status: 500,
    };
  }
};
